import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { OwnerGate } from './components/owner-gate';
import { Shell } from './components/shell';
import { queryClient } from './config/query-client';
import { isSupabaseConfigured } from './config/supabase';
import { useAuthInit } from './hooks/use-auth';
import { AlertsList } from './pages/alerts-list';
import { Analytics } from './pages/analytics';
import { AuditOwnerActions } from './pages/audit-owner-actions';
import { ChannelsList } from './pages/channels-list';
import { ContentArticles } from './pages/content-articles';
import { Costs } from './pages/costs';
import { Errors } from './pages/errors';
import { Infra } from './pages/infra';
import { Mobile } from './pages/mobile';
import { Notifications } from './pages/notifications';
import { Overview } from './pages/overview';
import { Placeholder } from './pages/placeholder';
import { UsersList } from './pages/users-list';
import { ThemeProvider } from './theme/theme-context';

import './theme/tokens.css';
import './theme/app.css';

/**
 * Section placeholders — each will be replaced with a full page in
 * later slices. Listed here so the routing tree and sidebar are
 * complete from day one and links don't 404.
 */
const PLACEHOLDERS: ReadonlyArray<{ path: string; title: string }> = [];

function AuthBoot({ children }: { readonly children: React.ReactNode }) {
  useAuthInit();
  return <>{children}</>;
}

function ConfigMissing() {
  return (
    <ThemeProvider>
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          padding: '2rem',
          background: 'var(--nw-bg)',
          color: 'var(--nw-fg)',
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <h1 style={{ marginBottom: '0.5rem' }}>Admin not configured</h1>
          <p style={{ opacity: 0.8, marginBottom: '1rem' }}>
            Set the following in <code>admin-web/.env.local</code> and restart
            the dev server:
          </p>
          <pre
            style={{
              padding: '1rem',
              background: '#0b1220',
              color: '#e6edf3',
              borderRadius: 8,
              overflowX: 'auto',
              fontSize: 13,
              lineHeight: 1.5,
              margin: 0,
            }}
          >{`VITE_SUPABASE_URL=https://<your-project>.supabase.co
VITE_SUPABASE_ANON_KEY=<anon-key>
VITE_ADMIN_API_BASE_URL=/owner
VITE_DEV_ADMIN_API_PROXY=http://localhost:3008`}</pre>
        </div>
      </div>
    </ThemeProvider>
  );
}

export function App() {
  if (!isSupabaseConfigured) {
    return <ConfigMissing />;
  }
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthBoot>
          <BrowserRouter>
            <OwnerGate>
              <Shell>
                <Routes>
                  <Route path="/" element={<Overview />} />
                  <Route path="/users" element={<UsersList />} />
                  <Route path="/content" element={<ContentArticles />} />
                  <Route path="/channels" element={<ChannelsList />} />
                  <Route path="/alerts" element={<AlertsList />} />
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/mobile" element={<Mobile />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/errors" element={<Errors />} />
                  <Route path="/infra" element={<Infra />} />
                  <Route path="/costs" element={<Costs />} />
                  <Route path="/audit" element={<AuditOwnerActions />} />
                  {PLACEHOLDERS.map(({ path, title }) => (
                    <Route
                      key={path}
                      path={path}
                      element={<Placeholder title={title} />}
                    />
                  ))}
                  <Route
                    path="*"
                    element={
                      <Placeholder
                        title="Not found"
                        description="That route doesn't exist yet."
                      />
                    }
                  />
                </Routes>
              </Shell>
            </OwnerGate>
          </BrowserRouter>
        </AuthBoot>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
